import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import promocode from '~/assets/icons/promocode.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	ISelectDiscountItem,
	ISelectDiscountProps,
} from './SelectDiscount.model';
import {
	StyledArrowDown,
	StyledContainer,
	StyledDropdownContainer,
	StyledItemsContainer,
	StyledSelectItem,
	StyledSelectItemWrapper,
	StyledTopContainer,
} from './SelectDiscount.styled';

const SelectDiscount: React.FC<ISelectDiscountProps> = ({
	items,
	value,
	onChange,
}) => {
	const [resultsVisible, setResultsVisible] = useState<boolean>(false);
	const resultsRef = useRef<HTMLDivElement>(null);

	const scrollbar = useMemo(() => items.length > 3, [items]);

	const text = useMemo(() => {
		if (value) {
			return (
				<Text style={{ fontSize: 16, fontWeight: 500, color: '#ffffff' }}>
					{value.discount}%{' '}
					<span style={{ opacity: 0.5, fontWeight: 400 }}>скидка</span>
				</Text>
			);
		}
		return (
			<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}>
				Выбрать скидку
			</Text>
		);
	}, [value]);

	const handleOpen = useCallback(() => {
		setResultsVisible((prevState) => !prevState);

		if (scrollbar) {
			setTimeout(() => {
				if (resultsRef.current) {
					(resultsRef.current as any)
						?.querySelector('.active')
						?.scrollIntoView();
				}
			}, 50);
		}
	}, [resultsRef, scrollbar]);

	const handleSelect = useCallback(
		(item: ISelectDiscountItem | null) => {
			if (!resultsVisible) {
				return;
			}

			onChange(item);
			setResultsVisible(false);
		},
		[onChange, resultsVisible],
	);

	useEffect(() => {
		function handleClickOutside(event: Event) {
			if (
				resultsRef.current &&
				!resultsRef.current.contains(event.target as Node)
			) {
				setResultsVisible(false);
			}
		}

		let timeoutId: NodeJS.Timeout;

		if (resultsVisible) {
			timeoutId = setTimeout(() => {
				document.addEventListener('click', handleClickOutside);
			}, 300);
		}

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('click', handleClickOutside);
		};
	}, [resultsRef, resultsVisible]);

	return (
		<StyledContainer ref={resultsRef}>
			<StyledTopContainer
				$focus={resultsVisible}
				$active={!!value}
				onClick={() => {
					handleOpen();
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			>
				<img src={promocode} style={{ opacity: value ? 1 : 0.25 }} />
				{text}
				<StyledArrowDown $active={resultsVisible} />
			</StyledTopContainer>
			<CSSTransition
				in={resultsVisible}
				classNames={'results'}
				timeout={500}
				unmountOnExit
			>
				<StyledDropdownContainer>
					<StyledItemsContainer tabIndex={-1}>
						{items.map((item, index) => (
							<StyledSelectItemWrapper
								key={index}
								onClick={() => {
									handleSelect(item);
									playSound(Sound.ClickButton);
								}}
								$scrollbar={scrollbar}
								$active={item === value}
								className={`${item === value ? 'active' : ''}`}
								onMouseEnter={() => {
									if (resultsVisible) {
										playSound(Sound.LinkCursor);
									}
								}}
							>
								{item?.discount && item.discount > 0 ? (
									<StyledSelectItem>
										<Text style={{ fontWeight: 500 }}>
											{item?.discount}%{' '}
											<span style={{ opacity: 0.5, fontWeight: 400 }}>
												скидка
											</span>
										</Text>
									</StyledSelectItem>
								) : (
									<StyledSelectItem>
										<Text style={{ fontWeight: 500 }}>
											<span style={{ opacity: 0.5, fontWeight: 400 }}>
												Без скидки
											</span>
										</Text>
									</StyledSelectItem>
								)}
							</StyledSelectItemWrapper>
						))}
					</StyledItemsContainer>
				</StyledDropdownContainer>
			</CSSTransition>
		</StyledContainer>
	);
};

export { SelectDiscount };
